import { Banner, Layout } from '@shopify/polaris';
import { useReviewPrompt } from '../hooks/useReviewPrompt';
import { useLanguage } from '../providers/LanguageProvider';

/**
 * Non-blocking, dismissible ask for an App Store review. Rendered inline as an
 * ordinary page section, never as a modal or an overlay, so it cannot obstruct
 * the page it appears on. Dismissing it costs the merchant nothing and is final.
 *
 * It owns its own Layout.Section so that a merchant who is not being asked gets
 * no markup at all, not an empty section spacing the page out.
 *
 * The copy names no star rating in either direction, because the Shopify App
 * Store prohibits explicitly asking for positive or five-star reviews.
 *
 * `source` names the surface asking. Any other legitimate moment can reuse this
 * component, or `useReviewPrompt` directly if it is not a Layout page, with no
 * change to the server-side caps.
 */
export default function ReviewPromptBanner({ source }: { source: string }) {
  const { visible, leaveReview, maybeLater, dismiss } = useReviewPrompt(source);
  const { t } = useLanguage();

  if (!visible) return null;

  return (
    <Layout.Section>
      <Banner
        title={t.reviewPrompt.title}
        tone="success"
        onDismiss={dismiss}
        action={{ content: t.reviewPrompt.leaveReview, onAction: leaveReview }}
        secondaryAction={{ content: t.reviewPrompt.maybeLater, onAction: maybeLater }}
      >
        <p>{t.reviewPrompt.body}</p>
      </Banner>
    </Layout.Section>
  );
}
